import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Link } from 'react-router-dom';

// Animations


const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -70%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Styled Components
const HeroContainer = styled.section`
  position: relative;
  height: 80vh;
  min-height: 600px;
  overflow: hidden;
  background-color: #1e3a8a;
`;

const HeroSlide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  opacity: ${props => (props.active ? '1' : '0')};
  transition: opacity 1.5s ease-in-out;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0.3) 50%,
      rgba(0, 0, 0, 0.1) 100%
    );
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  text-align: center;
  color: white;
  width: 90%;
  max-width: 800px;
  animation: ${slideDown} 1s ease-out;

`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #ffffff, #e0e0e0, #f5f5f5, #d3d3d3, #ffffff);
  background-size: 300% 100%;
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  animation: ${shimmer} 6s ease infinite;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BookNowButton = styled(Link)`
  display: inline-block;
  background-color: #f59e0b;
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: #d97706;
    transform: translateY(-3px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
    color: white;
  }
`;


const Hero = () => {
  const [heroData, setHeroData] = useState({
    images: ['https://res.cloudinary.com/dgfexk1y2/image/upload/v1753792582/sbxvuk0fs8siyfl41rw5.jpg'], // Start with default image
    title: 'Experience Luxury & Comfort',
    subtitle: 'Your perfect getaway in the heart of nature'
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [, setLoading] = useState(true);
  const [, setImagesLoaded] = useState(false);

  // Default image to show while loading
  const defaultImage = 'https://res.cloudinary.com/dgfexk1y2/image/upload/v1753792582/sbxvuk0fs8siyfl41rw5.jpg';

  useEffect(() => {
    const fetchHeroContent = async () => {
      try {
        setLoading(true);
        const heroContentCollection = collection(db, 'heroContent'); 
        const snapshot = await getDocs(heroContentCollection);
        const images = [defaultImage]; // Always include default image first
        let title = 'Experience Luxury & Comfort';
        let subtitle = 'Your perfect getaway in the heart of nature';
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          console.log(data,"data");
          if (data.type === 'image' && data.imageUrl) {
            images.push(data.imageUrl); // Add Firebase images after default
          } else if (data.type === 'text') {
            if (data.title) {
              title = data.title;
            }
            if (data.subtitle) {
              subtitle = data.subtitle;
            }
          }
        });
        
        // If no additional images were found in Firebase, add more default images
        if (images.length === 1) {
          images.push(
            'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
            'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80'
          );
        }
        
        setHeroData({
          images,
          title,
          subtitle
        });
        
        // Preload images before showing them
        if (images.length > 0) {
          const preloadImages = async () => {
            const imagePromises = images.map((src) => {
              return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = resolve;
                img.onerror = reject;
                img.src = src;
              });
            });
            
            try {
              await Promise.all(imagePromises);
              setImagesLoaded(true);
            } catch (error) {
              console.error('Error preloading images:', error);
              setImagesLoaded(true); // Still show content even if preloading fails
            }
          };
          
          preloadImages();
        } else {
          setImagesLoaded(true);
        }
        
        setLoading(false);
        localStorage.setItem('heroDataLoaded', 'true');
      } catch (error) {
        console.error('Error fetching hero content:', error);
        setLoading(false);
        localStorage.setItem('heroDataLoaded', 'true');
      }
    };
    
    fetchHeroContent();
  }, []);

  useEffect(() => {
    // Only start the interval if we have images
    if (heroData.images.length > 0) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroData.images.length);
      }, 6000); // Change image every 6 seconds (matches transition duration)

      return () => clearInterval(interval);
    }
  }, [heroData.images]);

  return (
    <HeroContainer>
      {heroData.images.map((image, index) => (
        <HeroSlide 
          key={index}
          image={image} 
          active={index === currentImageIndex}
        />
      ))}
        
      <HeroContent>
        <HeroTitle>{heroData.title}</HeroTitle>
        <HeroSubtitle>{heroData.subtitle}</HeroSubtitle>
        <BookNowButton to="/booking">Book Now</BookNowButton>
      </HeroContent>
    </HeroContainer>
  );
};

export default Hero;
